import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/components/shadcn/button';
import { Badge } from '@/shared/components/shadcn/badge';
import { cn } from '@/shared/utils';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import type { BenchmarkBrand, BenchmarkProduct, SalesPlatform } from '../data/benchmark-brands';
import { TIKTOK_SHOP_BRANDS } from '../data/benchmark-brands';

export interface BenchmarkSelection {
  selectedProductIds: Set<string>;
}

interface Props {
  salesPlatform: SalesPlatform;
  onNext: (selection: BenchmarkSelection) => void;
  onBack: () => void;
}

export function BenchmarkSelector({ salesPlatform, onNext, onBack }: Props) {
  const { workspace } = useWorkspace();
  const isKo = workspace.language === 'ko';

  const [expandedBrands, setExpandedBrands] = useState<Set<string>>(
    new Set(TIKTOK_SHOP_BRANDS.map((b) => b.name)),
  );
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set(TIKTOK_SHOP_BRANDS.flatMap((b) => b.products.filter((p) => !p.notRecommendedReason).map((p) => p.id))),
  );

  const brands = TIKTOK_SHOP_BRANDS; // both platforms use the same static data for now

  const toggleBrandExpand = (brandName: string) => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brandName)) next.delete(brandName);
      else next.add(brandName);
      return next;
    });
  };

  const toggleBrand = (brand: BenchmarkBrand) => {
    const productIds = brand.products.map((p) => p.id);
    const allSelected = productIds.every((id) => selectedProducts.has(id));
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        productIds.forEach((id) => next.delete(id));
      } else {
        productIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleProduct = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const getBrandState = (brand: BenchmarkBrand): 'all' | 'partial' | 'none' => {
    const ids = brand.products.map((p) => p.id);
    const selected = ids.filter((id) => selectedProducts.has(id)).length;
    if (selected === 0) return 'none';
    if (selected === ids.length) return 'all';
    return 'partial';
  };

  const selectedCount = selectedProducts.size;

  return (
    <div className="flex flex-col gap-6">
      {/* Brand Tree */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-semibold text-foreground">
              {isKo ? '벤치마크 브랜드 & 상품 선택' : 'Select Benchmark Brands & Products'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isKo ? '분석에 활용할 경쟁사 포스트 기반 데이터를 선택하세요' : 'Select competitor data to include in analysis'}
            </p>
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {isKo ? `${selectedCount}개 선택됨` : `${selectedCount} selected`}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          {brands.map((brand) => {
            const brandState = getBrandState(brand);
            const isExpanded = expandedBrands.has(brand.name);

            return (
              <div
                key={brand.name}
                className={cn(
                  'overflow-hidden rounded-xl border transition-colors',
                  brandState !== 'none' ? 'border-foreground/20 bg-card' : 'border-border bg-card',
                )}
              >
                {/* Brand header */}
                <div className="flex items-center gap-2.5 p-3">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleBrand(brand)}
                    className="shrink-0"
                    aria-label={`Select all ${brand.name} products`}
                  >
                    <div
                      className={cn(
                        'flex size-4 items-center justify-center rounded border-2 transition-colors',
                        brandState === 'all'
                          ? 'border-foreground bg-foreground'
                          : brandState === 'partial'
                            ? 'border-foreground bg-foreground/30'
                            : 'border-muted-foreground/40',
                      )}
                    >
                      {brandState === 'all' && (
                        <svg className="size-2.5 text-background" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5l2.5 2.5 3.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                      {brandState === 'partial' && (
                        <div className="size-1.5 rounded-sm bg-background" />
                      )}
                    </div>
                  </button>

                  {/* Brand image */}
                  <img
                    src={brand.imgPath}
                    alt={brand.name}
                    className="size-7 shrink-0 rounded-md object-contain border border-border bg-background"
                  />

                  {/* Brand name + reason */}
                  <button
                    className="flex flex-1 flex-col gap-0.5 text-left min-w-0"
                    onClick={() => toggleBrandExpand(brand.name)}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground truncate">{brand.name}</span>
                      <Badge variant="secondary" className="text-[10px] font-normal shrink-0">
                        {isKo ? `${brand.products.length}개 상품` : `${brand.products.length} products`}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground truncate">
                      {isKo ? brand.why : (brand.whyEn ?? brand.why)}
                    </span>
                  </button>

                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleBrandExpand(brand.name)}
                    className="shrink-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </button>
                </div>

                {/* Product list */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 px-3 py-2 flex flex-col gap-1.5">
                    {brand.products.map((product) => (
                      <ProductRow
                        key={product.id}
                        product={product}
                        isSelected={selectedProducts.has(product.id)}
                        onToggle={() => toggleProduct(product.id)}
                        isKo={isKo}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between gap-3 rounded-xl border bg-card p-4">
        <button
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={onBack}
        >
          {isKo ? '← 이전 단계' : '← Previous'}
        </button>
        <Button
          disabled={selectedCount === 0}
          onClick={() => onNext({ selectedProductIds: selectedProducts })}
          className="gap-2"
        >
          {isKo ? '분석 시작' : 'Start Analysis'}
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

function ProductRow({
  product,
  isSelected,
  onToggle,
  isKo,
}: {
  product: BenchmarkProduct;
  isSelected: boolean;
  onToggle: () => void;
  isKo: boolean;
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        'flex items-center gap-3 rounded-lg p-2 text-left transition-colors w-full',
        isSelected ? 'bg-card' : 'opacity-50 hover:opacity-70',
      )}
    >
      {/* Checkbox */}
      <div
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors',
          isSelected ? 'border-foreground bg-foreground' : 'border-muted-foreground/40',
        )}
      >
        {isSelected && (
          <svg className="size-2.5 text-background" viewBox="0 0 10 10" fill="none">
            <path d="M2 5l2.5 2.5 3.5-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      {/* Product image */}
      <img
        src={product.imgPath}
        alt={product.name}
        className="size-11 shrink-0 rounded-md object-contain border border-border bg-background"
      />

      {/* Product info */}
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-start gap-1.5 flex-wrap">
          <p className="text-xs font-medium text-foreground leading-snug line-clamp-2">{product.name}</p>
          {product.notRecommendedReason && (
            <span className="shrink-0 rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {isKo
                ? `비추천 · ${product.notRecommendedReason}`
                : `Not Recommended · ${product.notRecommendedReasonEn ?? product.notRecommendedReason}`}
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {isKo ? product.reason : (product.reasonEn ?? product.reason)}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          <span className="text-[10px] font-semibold text-foreground">{product.price}</span>
          <span className="text-[10px] text-muted-foreground">·</span>
          <span className="text-[10px] text-muted-foreground">6M Rev {product.revenue6m}</span>
          <span className="text-[10px] text-muted-foreground">·</span>
          <span className="text-[10px] text-muted-foreground">
            {isKo ? `판매 ${product.sales6m}` : `Sales ${product.sales6m}`}
          </span>
          <span className="text-[10px] text-muted-foreground">·</span>
          <span className="text-[10px] text-muted-foreground">{product.posts} posts</span>
        </div>
      </div>
    </button>
  );
}
